/**
 * MultiProviderMap - Composant de carte multi-provider (OSM/Mapbox)
 * 
 * Utilise Leaflet pour OpenStreetMap ou react-map-gl pour Mapbox
 * selon la configuration du backend.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Loader2 } from 'lucide-react';
import { getMapConfig, getLeafletConfig, isMapboxProvider } from '@/lib/mapService';

// Fix pour les icônes Leaflet avec webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Icône personnalisée pour le point de départ (vert)
const startIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Icône personnalisée pour le point d'arrivée (rouge)
const endIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Icône pour les étapes intermédiaires (orange)
const waypointIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

/**
 * Composant pour ajuster automatiquement la vue sur les marqueurs
 */
function FitBounds({ bounds }) {
  const map = useMap();
  
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [bounds, map]);
  
  return null;
}

/**
 * Composant MapView avec support multi-provider.
 * 
 * @param {Object} props
 * @param {Object} props.origin - Point d'origine {latitude, longitude, label}
 * @param {Object} props.destination - Point de destination {latitude, longitude, label}
 * @param {Array} props.waypoints - Points intermédiaires optionnels
 * @param {Array} props.routeCoordinates - Coordonnées de la route [[lng, lat], ...]
 * @param {Object} props.routeData - Données de route (distance_km, duration_hours)
 * @param {string} props.className - Classes CSS additionnelles
 * @param {number} props.height - Hauteur de la carte en pixels
 */
export default function MultiProviderMap({
  origin,
  destination,
  waypoints = [],
  routeCoordinates = [],
  routeData,
  className = '',
  height = 400
}) {
  const [mapConfig, setMapConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Charger la configuration au montage
  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await getMapConfig();
        setMapConfig(config);
      } catch (err) {
        console.error('Erreur chargement config carte:', err);
        setError('Erreur de configuration de la carte');
      } finally {
        setLoading(false);
      }
    }
    loadConfig();
  }, []);
  
  // Centre par défaut (Côte d'Ivoire)
  const defaultCenter = [7.5, -5.5];
  const defaultZoom = 6;
  
  // Calculer le centre et les bounds
  const center = origin 
    ? [origin.latitude, origin.longitude] 
    : defaultCenter;
  
  // Calculer les bounds pour le fitBounds
  const bounds = [];
  if (origin) bounds.push([origin.latitude, origin.longitude]);
  if (destination) bounds.push([destination.latitude, destination.longitude]);
  waypoints.forEach(wp => {
    if (wp.latitude && wp.longitude) {
      bounds.push([wp.latitude, wp.longitude]);
    }
  });
  
  // Convertir les coordonnées de route de [lng, lat] vers [lat, lng] pour Leaflet
  const polylinePositions = routeCoordinates.map(coord => [coord[1], coord[0]]);
  
  if (loading) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}
        style={{ height }}
      >
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Chargement de la carte...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div 
        className={`flex items-center justify-center bg-red-50 rounded-lg ${className}`}
        style={{ height }}
      >
        <div className="text-center text-red-600">
          <MapPin className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }
  
  if (!origin && !destination) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}
        style={{ height }}
      >
        <p className="text-gray-500 text-sm">
          Sélectionnez une origine et une destination pour afficher la carte
        </p>
      </div>
    );
  }
  
  const leafletConfig = getLeafletConfig(mapConfig);
  
  return (
    <div className="space-y-2">
      {/* Header avec infos de route */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900">Carte du Trajet</h3>
          <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
            {mapConfig?.provider === 'openstreetmap' ? 'OpenStreetMap' : 'Mapbox'}
          </span>
        </div>
        {routeData && (
          <div className="text-xs text-gray-600">
            <span className="font-semibold">{routeData.distance_km} km</span>
            {routeData.duration_hours && (
              <span> • {routeData.duration_hours}</span>
            )}
          </div>
        )}
      </div>
      
      {/* Conteneur de la carte */}
      <div 
        className={`relative rounded-lg overflow-hidden border border-gray-200 shadow-sm ${className}`}
        style={{ height }}
      >
        <MapContainer
          center={center}
          zoom={bounds.length > 1 ? defaultZoom : 12}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          {/* Tuiles de carte */}
          <TileLayer
            attribution={leafletConfig.attribution}
            url={leafletConfig.tileUrl}
            maxZoom={leafletConfig.maxZoom}
            subdomains={leafletConfig.subdomains}
          />
          
          {/* Ajuster la vue aux bounds */}
          {bounds.length > 1 && <FitBounds bounds={bounds} />}
          
          {/* Marqueur origine */}
          {origin && (
            <Marker 
              position={[origin.latitude, origin.longitude]} 
              icon={startIcon}
            >
              <Popup>
                <div className="text-sm">
                  <strong className="text-green-600">Départ</strong>
                  <br />
                  {origin.label || 'Point de départ'}
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* Marqueurs étapes intermédiaires */}
          {waypoints.map((wp, index) => (
            wp.latitude && wp.longitude && (
              <Marker
                key={`waypoint-${index}`}
                position={[wp.latitude, wp.longitude]}
                icon={waypointIcon}
              >
                <Popup>
                  <div className="text-sm">
                    <strong className="text-orange-600">Étape {index + 1}</strong>
                    <br />
                    {wp.label || `Étape ${index + 1}`}
                  </div>
                </Popup>
              </Marker>
            )
          ))}
          
          {/* Marqueur destination */}
          {destination && (
            <Marker 
              position={[destination.latitude, destination.longitude]} 
              icon={endIcon}
            >
              <Popup>
                <div className="text-sm">
                  <strong className="text-red-600">Arrivée</strong>
                  <br />
                  {destination.label || 'Point d\'arrivée'}
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* Ligne de trajet */}
          {polylinePositions.length > 1 && (
            <Polyline
              positions={polylinePositions}
              pathOptions={{
                color: '#3b82f6',
                weight: 5,
                opacity: 0.8
              }}
            />
          )}
        </MapContainer>
      </div>
      
      {/* Légende */}
      <div className="flex items-center gap-4 text-xs text-gray-600 px-2">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span>Départ</span>
        </div>
        {waypoints.length > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
            <span>Étapes ({waypoints.length})</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
          <span>Arrivée</span>
        </div>
        {polylinePositions.length > 1 && (
          <div className="flex items-center gap-1">
            <div className="w-8 h-0.5 bg-blue-500"></div>
            <span>Trajet</span>
          </div>
        )}
      </div>
    </div>
  );
}
